import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, Search, Separator} from 'platform/components';
import {Box, Heading, Hide, Show, Space, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {FieldValues, Path, useController, UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';

import {FeatureKey, VehicleTypeEnumObject} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {
  EMPTY_PLACEHOLDER,
  Nullish,
  RequiredTestIdProps,
  sortByAsciiNumberLast,
  suffixTestId,
} from 'shared/web';

import {FEATURE_SECTION_ORDER} from '../../constants/FEATURE_SECTION_ORDER';
import {useVehicleFeaturesV2} from '../../hooks/useVehicleFeaturesV2/useVehicleFeaturesV2';
import {groupFeaturesBySubCategory} from '../../utils/groupFeaturesBySubCategory';
import {FeatureTree} from './components/FeatureTree';

interface FeatureSelectorProps<
  TFieldValues extends FieldValues = FieldValues,
> extends RequiredTestIdProps {
  formApi: UseFormReturn<TFieldValues>;
  name: Path<TFieldValues>;
  vehicleType?: VehicleTypeEnumObject;
  columns?: number;
}

export function FeatureSelector<TFieldValues extends FieldValues>(
  props: FeatureSelectorProps<TFieldValues>
) {
  const {categorizedFeaturesTree, subCategories, search, setSearch, isLoading, isError} =
    useVehicleFeaturesV2(props.vehicleType);
  const isSubCategoriesEnabled = isFeatureEnabled(featureFlags.SALES_FEATURE_SUBCATEGORIES);

  const {field} = useController({control: props.formApi.control, name: props.name});
  const formFeatures: string[] = field.value || [];

  const handleTreeFeatureChange = (oldFeature: string | Nullish, newFeature: string) => {
    if (isNil(oldFeature)) {
      field.onChange([...formFeatures, newFeature]);
      return;
    }

    if (oldFeature === newFeature) {
      field.onChange(formFeatures.filter((feature) => feature !== oldFeature));
      return;
    }

    field.onChange([...formFeatures.filter((feature) => feature !== oldFeature), newFeature]);
  };

  const saneSections = categorizedFeaturesTree.filter((item) => item.features.length);
  const alphabeticallyOrderedSections = saneSections
    .map((section) => ({
      ...section,
      features: section.features.toSorted((a, b) => {
        if (a?.labels?.length > 0 && b?.labels?.length > 0) {
          return sortByAsciiNumberLast(a.labels[0].label, b.labels[0].label);
        } else {
          console.warn('[FeatureSelector] Feature has no labels', a, b);
        }
        return 0;
      }),
    }))
    .toSorted((a, b) => {
      const indexA = FEATURE_SECTION_ORDER.indexOf(a.key);
      const indexB = FEATURE_SECTION_ORDER.indexOf(b.key);

      if (indexA === -1 && indexB === -1) {
        return 0;
      }
      if (indexA === -1) {
        return 1;
      }
      if (indexB === -1) {
        return -1;
      }

      return indexA - indexB;
    });

  const getTreeSelectedFeature = (section: Section, topLevelFeature: FeatureKey) =>
    formFeatures.find(
      (selected) => section.treeOwnerMap.get(selected) === topLevelFeature.const_key
    );

  const renderTrees = (section: Section, trees: FeatureKey[]) => (
    <ColumnsDiv columns={props.columns}>
      {trees.map((topLevelFeature) => (
        <FeatureTree
          key={topLevelFeature.const_key}
          feature={topLevelFeature}
          selectedFeature={getTreeSelectedFeature(section, topLevelFeature)}
          onChange={handleTreeFeatureChange}
          data-testid={suffixTestId(`tree-${section.features.indexOf(topLevelFeature)}`, props)}
        />
      ))}
    </ColumnsDiv>
  );

  const renderSubCategories = (section: Section) => {
    const groups = groupFeaturesBySubCategory({
      items: section.features,
      categoryKey: section.key,
      subCategories,
      getSubCategory: (feature) => feature.sub_category,
    });
    const hasHeadings = groups.some((group) => !group.isOther);

    return (
      <VStack spacing={6}>
        {groups.map((group) => (
          <VStack
            key={group.key}
            spacing={3}
            data-testid={suffixTestId(`subCategory-${section.key}-${group.key}`, props)}
          >
            <Show when={hasHeadings}>
              <Heading size={6} color="secondary">
                {group.label ?? i18n.t('entity.vehicle.labels.otherLabel')}
              </Heading>
            </Show>
            {renderTrees(section, group.items)}
          </VStack>
        ))}
      </VStack>
    );
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <VStack>
        <Search
          value={search || null}
          onChange={(newValue) => setSearch(newValue || undefined)}
          data-testid={suffixTestId('search', props)}
        />
        <Space vertical={4} />
        {alphabeticallyOrderedSections.map((section, index) => (
          <Box key={section.key}>
            <Heading size={4}>{section.category?.labels?.[0]?.label ?? EMPTY_PLACEHOLDER}</Heading>
            <Space vertical={4} />
            {match(isSubCategoriesEnabled)
              .with(true, () => renderSubCategories(section))
              .otherwise(() => renderTrees(section, section.features))}
            <Hide when={alphabeticallyOrderedSections.length - 1 === index}>
              <Separator />
            </Hide>
          </Box>
        ))}
      </VStack>
    </DataStatus>
  );
}

type Section = ReturnType<typeof useVehicleFeaturesV2>['categorizedFeaturesTree'][number];

const ColumnsDiv = styled.div<{columns?: number}>`
  columns: ${({columns}) => columns || 3};
`;
